import { leaveEndedAdminAccess } from '@/auth/admin-access-navigation';
import { getCurrentUser, logout, refreshTokenApi } from '@/auth/auth-service';
import {
  getAccessToken,
  getAuthData,
  isAdminAccessTab,
  saveAuthData,
  saveTokens,
} from '@/auth/auth-storage';
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import toast from 'react-hot-toast';

// 동시에 여러 401이 발생해도 refresh는 1번만 호출되도록 singleton promise
let refreshPromise: ReturnType<typeof refreshTokenApi> | null = null;

// 비밀번호 변경 요구로 동시에 여러 요청이 막혀도 동기화는 1번만
let passwordStateSyncPromise: Promise<void> | null = null;

// API URL 설정
const API_URL = import.meta.env.PUBLIC_API_URL;

// API 클라이언트 싱글톤 인스턴스
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

/**
 * 로그인 세션이 있어도 URL의 redirectToken을 우선하는 경로.
 *
 * 대부분의 public 페이지는 로그인 상태면 본인 계정으로 접근하는 것이 맞다.
 * 서버 usecase도 매직링크 경로와 로그인 경로를 모두 지원하며 소유권을 검증한다.
 *
 * 반면 LCD(Leader Context Document) 작성 폼은 링크를 받은 사람(상위리더)의 자격으로만
 * 접근할 수 있고, 그 사람이 우리 콘솔 계정을 이미 갖고 있을 수 있다. 로그인 토큰이 실려
 * 나가면 서버가 매직링크로 인식하지 못해 본인이 받은 링크인데도 403이 된다.
 */
const MAGIC_LINK_FIRST_PATHS = ['/public/leader-context-document'];

// 요청 인터셉터 - 토큰 추가
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    const redirectToken = new URLSearchParams(window.location.search).get(
      'redirectToken',
    );
    const prefersMagicLink =
      !!redirectToken &&
      MAGIC_LINK_FIRST_PATHS.includes(window.location.pathname);

    if (prefersMagicLink) {
      config.headers.Authorization = `Bearer ${redirectToken}`;
    } else if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (redirectToken) {
      // 로그인되지 않은 상태 (public 페이지): URL의 redirectToken을 Bearer로 사용
      config.headers.Authorization = `Bearer ${redirectToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// 응답 인터셉터 - 토큰 갱신 및 에러 처리
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    // 대리 접속 탭은 리프레시 토큰이 없다. 일반 처리대로 로그아웃하면 원래 탭의 로그인까지
    // 지워지므로, 갱신을 시도하지 않고 종료 화면으로 보낸다.
    if (error.response?.status === 401 && isAdminAccessTab()) {
      leaveEndedAdminAccess();
      return Promise.reject(error);
    }

    // 401 에러 처리 (토큰 만료)
    // magic link 페이지(redirectToken이 URL에 있음)는 refresh 없이 로그인 페이지로 이동
    const isMagicLinkPage = !!new URLSearchParams(window.location.search).get(
      'redirectToken',
    );
    // 외부인(우리 계정이 없을 수 있는 사람)에게 보내는 링크는 로그인 페이지로 보내지 않는다.
    // 페이지가 직접 안내 화면을 그리도록 에러를 그대로 넘긴다.
    const isExternalMagicLinkPage = MAGIC_LINK_FIRST_PATHS.includes(
      window.location.pathname,
    );
    if (error.response?.status === 401 && isExternalMagicLinkPage) {
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && isMagicLinkPage) {
      const redirectTo = window.location.pathname + window.location.search;
      window.location.href = `/auth/login?redirect=${encodeURIComponent(redirectTo)}`;
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // 이미 다른 요청이 토큰을 갱신했으면 refresh 없이 새 토큰으로 재시도
      const currentToken = getAccessToken();
      const authHeader = originalRequest.headers?.Authorization;
      const requestToken =
        typeof authHeader === 'string'
          ? authHeader.replace('Bearer ', '')
          : null;
      if (currentToken && requestToken && currentToken !== requestToken) {
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${currentToken}`;
        }
        return axiosInstance(originalRequest);
      }

      try {
        // 동시에 여러 401이 발생해도 refresh API는 1번만 호출
        if (!refreshPromise) {
          refreshPromise = refreshTokenApi().finally(() => {
            refreshPromise = null;
          });
        }
        const tokens = await refreshPromise;

        // 새 토큰 localStorage에 저장 (저장 안 하면 다음 요청도 만료된 토큰 사용)
        saveTokens(tokens);

        // 새 토큰으로 헤더 설정
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
        }

        // 원래 요청 재시도
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // 리프레시 실패 시 로그아웃
        logout();

        // 로그인 페이지로 리다이렉트
        const redirectTo = window.location.pathname + window.location.search;
        window.location.href = `/auth/login?redirect=${encodeURIComponent(redirectTo)}`;
        return Promise.reject(refreshError);
      }
    }

    // 비밀번호 변경 주기가 지나 서버가 차단한 경우 (ISMS-P 2.1.1).
    // 저장된 사용자 정보가 아직 변경 전 상태라 화면이 안내를 못 띄우므로,
    // 서버가 판정한 최신 상태를 받아와 변경 화면이 뜨게 한다.
    const errorCode = (
      error.response?.data as { errorCode?: string } | undefined
    )?.errorCode;
    if (
      error.response?.status === 403 &&
      errorCode === 'PASSWORD_CHANGE_REQUIRED'
    ) {
      await syncPasswordChangeState();
      return Promise.reject(error);
    }

    // 비밀번호 변경·동의·설문 응답처럼 본인만 할 수 있는 동작은 대리 접속 중에 서버가 막는다.
    if (
      error.response?.status === 403 &&
      errorCode === 'ADMIN_ACCESS_FORBIDDEN_ACTION'
    ) {
      toast.error(
        '본인만 할 수 있는 동작이라 대리 접속 중에는 할 수 없습니다.',
      );
      return Promise.reject(error);
    }

    // 서버 에러 발생 시 (5xx)
    if (error.response?.status && error.response.status >= 500) {
      toast.error('서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.');
    }

    return Promise.reject(error);
  },
);

/**
 * 서버가 비밀번호 변경을 요구했을 때 저장된 사용자 정보를 최신 상태로 맞춘다.
 * 상태가 실제로 바뀌었을 때만 새로고침해, 이미 변경 화면을 보고 있는 경우에는
 * 화면이 반복해서 다시 그려지지 않게 한다.
 */
function syncPasswordChangeState(): Promise<void> {
  if (passwordStateSyncPromise) return passwordStateSyncPromise;

  passwordStateSyncPromise = (async () => {
    try {
      const stored = getAuthData();
      if (!stored?.user) return;

      const user = await getCurrentUser();
      const nextState = user?.passwordChange?.state;
      if (!nextState || nextState === stored.user.passwordChange?.state) return;

      saveAuthData({
        ...stored,
        user: { ...stored.user, passwordChange: user.passwordChange },
      });
      window.location.reload();
    } catch {
      // 조회마저 막히면 다음 요청에서 다시 시도한다
    } finally {
      passwordStateSyncPromise = null;
    }
  })();

  return passwordStateSyncPromise;
}

// Orval에서 사용할 클라이언트 함수
export const apiClient = <T>(config: AxiosRequestConfig): Promise<T> => {
  return axiosInstance(config).then(
    (response: AxiosResponse<T>) => response.data,
  );
};

export default axiosInstance;
